/**
 * B1126 (Eric 2026-09-08) — Mise en forme des tags : acronyme, casse du
 * premier mot, singulier, pluriel.
 *
 * POURQUOI CÔTÉ CLIENT
 * ────────────────────
 * Ces transformations doivent s'afficher AVANT d'être appliquées (le bouton
 * dit ce qu'il va faire, la confirmation montre le résultat). Un aller-retour
 * serveur pour calculer « REER » à partir de « reer » rendrait l'interface
 * hésitante pour rien. Le renommage lui-même, lui, passe par le serveur.
 *
 * PLURIEL FRANÇAIS
 * ────────────────
 * Les règles régulières couvrent la quasi-totalité du vocabulaire métier
 * (« placement », « fiducie », « taux »). Les exceptions réelles du domaine
 * financier sont listées : « capital → capitaux », pas « capitals ».
 */

// Mots où la règle régulière donne un résultat faux. Volontairement court :
// une liste de 200 mots deviendrait fausse et impossible à maintenir.
const IRREGULAR_PLURALS = {
  capital: "capitaux",
  bail: "baux",
  travail: "travaux",
  oeil: "yeux",
  "œil": "yeux",
  bijou: "bijoux",
  caillou: "cailloux",
  genou: "genoux",
  hibou: "hiboux",
  chou: "choux",
  pou: "poux",
  joujou: "joujoux",
  monsieur: "messieurs",
  madame: "mesdames",
};

const IRREGULAR_SINGULARS = Object.fromEntries(
  Object.entries(IRREGULAR_PLURALS).map(([s, p]) => [p, s])
);

// Finales invariables : « taux », « choix », « nez » n'ont pas de pluriel
// distinct. Ajouter un « s » produirait « tauxs ».
const INVARIABLE_ENDINGS = ["s", "x", "z"];

// Mots qui S'ÉCRIVENT avec un « s » au singulier : sans cette liste,
// « fonds » (mot central du vocabulaire d'Eric) devenait « fond ».
const INVARIABLE_WORDS = new Set([
  "fonds", "taux", "choix", "cours", "corps", "temps", "prix", "mois",
  "pays", "avis", "cas", "sens", "dos", "sous",
]);

const words = (tag) => String(tag || "").split(/(\s+)/);

const applyToFirstWord = (tag, fn) => {
  const parts = words(tag);
  const i = parts.findIndex((p) => p.trim().length > 0);
  if (i === -1) return String(tag || "");
  parts[i] = fn(parts[i]);
  return parts.join("");
};

// Tâche 1267 (Eric 2026-06-28) — ACCORD COMPLET. Le nombre ne se portait que
// sur le DERNIER mot : « placement garanti » devenait « placement garantis ».
// En français, le nom ET les adjectifs qui le suivent s'accordent ensemble.
// On accorde donc tous les mots du groupe de tête, et on s'arrête net au
// premier mot de liaison : « taux de rendement » ne doit pas devenir
// « taux de rendements ».
const STOP_WORDS = new Set([
  "de", "du", "des", "d", "à", "a", "au", "aux", "en", "et", "ou", "pour",
  "sur", "par", "avec", "sans", "dans", "chez", "le", "la", "les", "l",
  "un", "une", "son", "sa", "ses", "ce", "cet", "cette", "que", "qui",
]);

const applyToHeadGroup = (tag, fn) => {
  const parts = words(tag);
  let touched = 0;
  for (let i = 0; i < parts.length; i += 1) {
    const w = parts[i];
    if (!w.trim().length) continue;
    const bare = w.replace(/^[('"«]+|[)'"»,;:.]+$/g, "");
    if (STOP_WORDS.has(bare.toLocaleLowerCase("fr-CA").replace(/['’]$/, ""))) break;
    parts[i] = fn(w);
    touched += 1;
  }
  return touched ? parts.join("") : String(tag || "");
};

const applyToLastWord = (tag, fn) => {
  const parts = words(tag);
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    if (parts[i].trim().length > 0) {
      parts[i] = fn(parts[i]);
      return parts.join("");
    }
  }
  return String(tag || "");
};

/** Tout en majuscules — « reer » → « REER ». */
export const toAcronym = (tag) => String(tag || "").toLocaleUpperCase("fr-CA");

/** Majuscule au premier mot — « reer collectif » → « Reer collectif ». */
export const capitalizeFirstWord = (tag) =>
  applyToFirstWord(tag, (w) => w.charAt(0).toLocaleUpperCase("fr-CA") + w.slice(1));

/** Minuscule au premier mot — « Reer collectif » → « reer collectif ». */
export const lowercaseFirstWord = (tag) =>
  applyToFirstWord(tag, (w) => w.charAt(0).toLocaleLowerCase("fr-CA") + w.slice(1));

// Un sigle est invariable en français : on écrit « des REER », « des CELI »,
// jamais « des REERs ». Sans cette règle, le bouton « pluriel » abîmerait
// justement les tags les plus fréquents du vocabulaire d'Eric.
// Longueur ≤ 5 : REER, CELI, FERR, RRQ, FNB… Au-delà, un mot tout en
// majuscules est presque toujours un mot ordinaire crié (« CAPITAL »), qui
// doit garder son pluriel (« CAPITAUX »).
const ACRONYM_MAX = 5;
const isAcronym = (w) => w.length >= 2 && w.length <= ACRONYM_MAX
  && w === w.toLocaleUpperCase("fr-CA") && /[A-ZÀ-Ý]/.test(w);

/** Met le groupe de tête au pluriel : nom ET adjectifs qui le suivent. */
export const toPlural = (tag) =>
  applyToHeadGroup(tag, (w) => {
    if (isAcronym(w)) return w;
    const lower = w.toLocaleLowerCase("fr-CA");
    if (INVARIABLE_WORDS.has(lower)) return w;
    const irregular = IRREGULAR_PLURALS[lower];
    if (irregular) return matchCase(w, irregular);
    if (INVARIABLE_ENDINGS.some((e) => lower.endsWith(e))) return w;
    if (lower.endsWith("al")) return matchCase(w, `${lower.slice(0, -2)}aux`);
    if (lower.endsWith("au") || lower.endsWith("eu")) return matchCase(w, `${lower}x`);
    return matchCase(w, `${lower}s`);
  });

/** Met le groupe de tête au singulier : nom ET adjectifs qui le suivent. */
export const toSingular = (tag) =>
  applyToHeadGroup(tag, (w) => {
    if (isAcronym(w)) return w;
    const lower = w.toLocaleLowerCase("fr-CA");
    if (INVARIABLE_WORDS.has(lower)) return w;
    const irregular = IRREGULAR_SINGULARS[lower];
    if (irregular) return matchCase(w, irregular);
    // « bureaux » → « bureau », mais « taux » et « faux » sont invariables :
    // sans le test de longueur, « taux » devenait « tal ».
    if (lower.endsWith("eaux")) return matchCase(w, lower.slice(0, -1));
    if (lower.endsWith("aux") && lower.length > 4) {
      return matchCase(w, `${lower.slice(0, -3)}al`);
    }
    if (lower.endsWith("eux") && lower.length > 4) {
      return matchCase(w, lower.slice(0, -1));
    }
    if (INVARIABLE_ENDINGS.slice(1).some((e) => lower.endsWith(e))) return w;  // x, z
    if (lower.endsWith("s") && lower.length > 2 && !lower.endsWith("ss")) {
      return matchCase(w, lower.slice(0, -1));
    }
    return w;
  });

/** Conserve la casse d'origine (MAJUSCULES, Capitale, minuscules). */
export function matchCase(original, transformed) {
  if (original === original.toLocaleUpperCase("fr-CA") && /[A-ZÀ-Ý]/.test(original)) {
    return transformed.toLocaleUpperCase("fr-CA");
  }
  if (original.charAt(0) === original.charAt(0).toLocaleUpperCase("fr-CA")) {
    return transformed.charAt(0).toLocaleUpperCase("fr-CA") + transformed.slice(1);
  }
  return transformed;
}

// Tâche 1429 — Ces libellés sont les BOUTONS de transformation du vocabulaire,
// affichés sur chaque ligne de tag : ils restaient en français dans la version
// anglaise de « Recherche par tags ». Ils portent donc leur version anglaise,
// comme les étapes du pipeline (1378) et les états du journal (1420).
export const TAG_TRANSFORMS = {
  acronym: {
    label: "acronyme", label_en: "acronym",
    title: "Tout en majuscules", title_en: "All uppercase",
    apply: toAcronym,
  },
  upperFirst: {
    label: "majuscule au 1er mot",
    label_en: "capital on 1st word",
    title: "Ajoute une majuscule au premier mot",
    title_en: "Capitalises the first word",
    apply: capitalizeFirstWord,
  },
  lowerFirst: {
    label: "minuscule au 1er mot",
    label_en: "lowercase 1st word",
    title: "Change la majuscule du premier mot en minuscule",
    title_en: "Turns the first word's capital into a lowercase letter",
    apply: lowercaseFirstWord,
  },
  singular: {
    label: "singulier", label_en: "singular",
    title: "Met le tag au singulier", title_en: "Puts the tag in the singular",
    apply: toSingular,
  },
  plural: {
    label: "pluriel", label_en: "plural",
    title: "Met le tag au pluriel", title_en: "Puts the tag in the plural",
    apply: toPlural,
  },
};

/** Libellé et explication d'une transformation, dans la langue demandée. */
export const transformLabel = (transform, lang) =>
  (lang === "en" ? transform.label_en : transform.label) || transform.label;

export const transformTitle = (transform, lang) =>
  (lang === "en" ? transform.title_en : transform.title) || transform.title;


/**
 * B1128 (Eric 2026-09-08) — Détecte les tags qui existent en PLUSIEURS
 * GRAPHIES (« REER », « Reer », « reers ») et propose de les unifier.
 *
 * Deux graphies d'un même mot coupent le vocabulaire en deux : une recherche
 * sur l'une ne trouve pas les vidéos de l'autre, et personne ne s'en aperçoit.
 * La clé de regroupement ignore la casse, les accents et le nombre — ce sont
 * exactement les trois différences qu'un humain ne considère pas comme deux
 * mots distincts.
 */
export const normalizeTagKey = (tag) => {
  const singular = toSingular(String(tag || "").trim());
  return singular
    .toLocaleLowerCase("fr-CA")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")   // accents
    .replace(/[\s-]+/g, " ")             // espaces multiples, traits d'union
    .trim();
};

/**
 * Regroupe les tags par graphie équivalente.
 * `tags` : [{ tag, count }]. Retourne les groupes de 2 variantes ou plus,
 * la variante la PLUS UTILISÉE en tête (c'est la forme que l'équipe a
 * réellement adoptée : la choisir d'office évite un débat).
 */
export const findVariantGroups = (tags = []) => {
  const groups = new Map();
  tags.forEach((t) => {
    const key = normalizeTagKey(t.tag);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ tag: t.tag, count: t.count || 0 });
  });
  return [...groups.entries()]
    .filter(([, variants]) => variants.length > 1)
    .map(([key, variants]) => {
      const sorted = [...variants].sort(
        (a, b) => (b.count - a.count) || a.tag.localeCompare(b.tag, "fr-CA"));
      return {
        key,
        canonical: sorted[0].tag,
        variants: sorted,
        total: sorted.reduce((n, v) => n + v.count, 0),
      };
    })
    .sort((a, b) => b.total - a.total);
};
